/**
 * UTM Parameter Utility
 * 
 * This utility captures UTM parameters from the URL on first page load
 * and persists them in localStorage so they are available across page navigation
 * in Gatsby. The parameters are then retrieved when forms are submitted.
 * 
 * UTM parameters expire after 15 days to prevent stale attribution data.
 */

const UTM_STORAGE_KEY = 'rmappf_utm_params';
const UTM_PARAMS = ['cm_id', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const UTM_EXPIRATION_DAYS = 15; // UTM parameters expire after 15 days
const UTM_EXPIRATION_MS = UTM_EXPIRATION_DAYS * 24 * 60 * 60 * 1000; // Convert to milliseconds

// Object with every tracked parameter set to an empty string
export const emptyUTMParams = () =>
  UTM_PARAMS.reduce((acc, param) => ({ ...acc, [param]: '' }), {});

/**
 * Captures UTM parameters from the current URL query string
 * and stores them in localStorage if they exist.
 * Only updates storage if new UTM parameters are found in the URL.
 * 
 * This should be called on page load/navigation to capture UTM params.
 */
export const captureUTMParams = () => {
  // SSR safety check
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Check if existing stored UTM parameters are expired and clear them
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const timestamp = parsed._timestamp;
        // If stored data is expired, clear it
        if (timestamp && Date.now() - timestamp > UTM_EXPIRATION_MS) {
          localStorage.removeItem(UTM_STORAGE_KEY);
        }
      } catch (parseError) {
        // If data is corrupted, clear it
        localStorage.removeItem(UTM_STORAGE_KEY);
      }
    }

    const queryParams = new URLSearchParams(window.location.search);
    const utmData = {};
    let hasUTMParams = false;

    // Extract all UTM parameters from URL
    UTM_PARAMS.forEach(param => {
      const value = queryParams.get(param);
      if (value) {
        utmData[param] = value;
        hasUTMParams = true;
      }
    });

    // Only update localStorage if we found UTM parameters in the URL
    // This ensures we preserve existing UTM params if user navigates without new UTM params
    if (hasUTMParams) {
      // Store UTM data with timestamp for expiration tracking
      const dataWithTimestamp = {
        ...utmData,
        _timestamp: Date.now(), // Store current timestamp
      };
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(dataWithTimestamp));
    }
  } catch (error) {
    console.error('Error capturing UTM parameters:', error);
  }
};

/**
 * Retrieves persisted UTM parameters from localStorage.
 * Returns an object with all UTM parameters (empty strings if not found or expired).
 * Automatically clears expired UTM parameters (older than 15 days).
 * 
 * @returns {Object} Object containing cm_id, utm_source, utm_medium, utm_campaign, utm_content, utm_term
 */
export const getUTMParams = () => {
  // SSR safety check
  if (typeof window === 'undefined') {
    return emptyUTMParams();
  }

  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const timestamp = parsed._timestamp;
      
      // Check if UTM parameters have expired (older than 15 days)
      if (timestamp && Date.now() - timestamp > UTM_EXPIRATION_MS) {
        // Clear expired UTM parameters
        localStorage.removeItem(UTM_STORAGE_KEY);
        // Return empty strings for expired data
        return emptyUTMParams();
      }
      
      // Return stored values or empty strings for missing params
      // Exclude the internal _timestamp field from the returned object
      return UTM_PARAMS.reduce(
        (acc, param) => ({ ...acc, [param]: parsed[param] || '' }),
        {}
      );
    }
  } catch (error) {
    console.error('Error retrieving UTM parameters:', error);
    // Clear corrupted data
    try {
      localStorage.removeItem(UTM_STORAGE_KEY);
    } catch (clearError) {
      console.error('Error clearing corrupted UTM parameters:', clearError);
    }
  }

  // Return empty strings if no stored data or error occurred
  return emptyUTMParams();
};

/**
 * Clears stored UTM parameters from localStorage.
 * Useful for testing or when you want to reset UTM tracking.
 */
export const clearUTMParams = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing UTM parameters:', error);
  }
};

/**
 * Debug function to check what's stored in localStorage
 * Useful for troubleshooting UTM parameter issues
 */
export const debugUTMStorage = () => {
  if (typeof window === 'undefined') {
    console.log('UTM Debug: window is undefined (SSR)');
    return;
  }

  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    console.log('UTM Debug - Raw localStorage:', stored);
    if (stored) {
      const parsed = JSON.parse(stored);
      console.log('UTM Debug - Parsed data:', parsed);
      console.log('UTM Debug - Timestamp:', parsed._timestamp);
      console.log('UTM Debug - Age (ms):', Date.now() - (parsed._timestamp || 0));
      console.log('UTM Debug - Expired?', parsed._timestamp && Date.now() - parsed._timestamp > UTM_EXPIRATION_MS);
    } else {
      console.log('UTM Debug - No data in localStorage');
    }
  } catch (error) {
    console.error('UTM Debug - Error:', error);
  }
};

